import React, { useEffect, useRef, useState } from "react";
import { Text } from "src/sudokit/components/Text";
import { COVER_MAX_ZOOM, CropView, getCropRegion, initialCropView, panCropView, Size, zoomCropView } from "./cropRect";
import { loadImage } from "./cropCoverImage";

interface CoverCropperProps {
  src: string;
  view: CropView | null;
  onViewChange: (view: CropView) => void;
  onImageSize: (size: Size) => void;
  imageSize: Size | null;
  onLoadError: () => void;
}

export default function CoverCropper({ src, view, onViewChange, onImageSize, imageSize, onLoadError }: CoverCropperProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadImage(src)
      .then((image) => {
        if (cancelled) return;
        const size = { width: image.naturalWidth, height: image.naturalHeight };
        onImageSize(size);
        onViewChange(initialCropView(size));
      })
      .catch(() => {
        if (!cancelled) onLoadError();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const ready = !!imageSize && !!view;
  const region = ready ? getCropRegion(imageSize, view) : null;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!ready) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY };
    setDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const last = dragRef.current;
    const frameWidth = frameRef.current?.clientWidth;
    if (!last || !ready || !frameWidth) return;
    dragRef.current = { x: event.clientX, y: event.clientY };
    onViewChange(panCropView(imageSize, view, { x: event.clientX - last.x, y: event.clientY - last.y }, frameWidth));
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  const setZoom = (zoom: number) => {
    if (ready) onViewChange(zoomCropView(imageSize, view, zoom));
  };

  return (
    <div className="mt-3 flex flex-col gap-3">
      <div
        ref={frameRef}
        data-testid="cover-cropper-frame"
        className={`relative aspect-[2/3] w-full touch-none select-none overflow-hidden rounded-md bg-app-menu-color-hover ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onWheel={(event) => ready && setZoom(view.zoom - event.deltaY * 0.004)}
      >
        {region && imageSize && (
          <img
            src={src}
            alt="Cover being cropped"
            draggable={false}
            className="pointer-events-none absolute max-w-none"
            style={{
              width: `${(imageSize.width / region.width) * 100}%`,
              left: `${(-region.x / region.width) * 100}%`,
              top: `${(-region.y / region.height) * 100}%`,
            }}
          />
        )}
        <div className="pointer-events-none absolute inset-0 rounded-md shadow-[inset_0_0_0_1px_rgba(255,255,255,0.35)]">
          <div className="absolute inset-y-0 left-1/3 w-px bg-white/25" />
          <div className="absolute inset-y-0 left-2/3 w-px bg-white/25" />
          <div className="absolute inset-x-0 top-1/3 h-px bg-white/25" />
          <div className="absolute inset-x-0 top-2/3 h-px bg-white/25" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          aria-label="Zoom"
          min={1}
          max={COVER_MAX_ZOOM}
          step={0.01}
          disabled={!ready}
          value={view?.zoom ?? 1}
          onChange={(event) => setZoom(Number(event.target.value))}
          className="h-1 w-full cursor-pointer accent-[var(--primary-cta)]"
        />
      </div>
      <Text
        size="sm"
        className="text-center text-app-grey-1"
      >
        Covers are 2:3. Drag to reposition, scroll or use the slider to zoom.
      </Text>
    </div>
  );
}
